import { LanguageCodeByBranchCode } from '@/helpers/i18n';
import { UtilityHelper } from '@/helpers/UtilityHelper';
import i18n from '@/i18n';
import store from '@/store';
import * as amplitude from '@amplitude/analytics-browser';
import CryptoJS from 'crypto-js';

export const routeAnalyticsEvents: Record<string, string> = {
    newLogin: 'LOGIN',
    restart: 'LOGIN',
    newAccount: 'RESIDENCE',
    mobileSms: 'PROOF_OF_IDENTITY',
    mobileDocumentUpload: 'PROOF_OF_IDENTITY', //Mobile
    personalDataConfirm: 'PERSONAL_DETAILS',
    desktopSms: 'PHONE_NUMBER',
    addressDetails: 'ADDRESS',
    chooseAccountType: 'CHOOSE_ACCOUNT_TYPE',
    taxOfficeDetails: 'TAX_RESIDENCY',
    currencyChoice: 'CURRENCY',
    riskAssessment: 'FINANCIAL_PROFILE',
    declarationAcceptance: 'STATEMENTS',
    esgSubmit: 'ESG_SUBMIT',
    errorsReport: 'GO_TO_CLIENT_ZONE',
    uploadSucceeded: 'PROOF_OF_IDENTITY_UPLOAD_SUCCEEDED',
    uploadFailed: 'PROOF_OF_IDENTITY_UPLOAD_FAILED',
    uploadException: 'PROOF_OF_IDENTITY_UPLOAD_EXCEPTION',
    mobileDesktopIsCompleted: 'WAITING_FOR_MOBILE',
    mobileReturnToDesktop: 'MOBILE_RETURN_TO_DESKTOP',
};

export const AmplitudeAnalyticsEvents = {
    taxResidence: {
        TAX_RESIDENCY_MORE_THAN_ONE: 'TAX_RESIDENCY_MORE_THAN_ONE',
        TAX_RESIDENCY_CONTINUE: 'TAX_RESIDENCY_CONTINUE',
    },

    newAccount: {
        ACCOUNT_CREATED: 'ACCOUNT_CREATED',
    },

    newLogin: {
        LANGUAGE: 'LANGUAGE',
        RESIDENCE_CONTINUE: 'RESIDENCE_CONTINUE',
        GO_TO_LOGIN: 'GO_TO_LOGIN',
        EMAIL: 'EMAIL',
        EMAIL_CONTINUE: 'EMAIL_CONTINUE',
        PASSWORD: 'PASSWORD',
        PASSWORD_CONTINUE: 'PASSWORD_CONTINUE',
        LOGIN_CONTINUE: 'LOGIN_CONTINUE',
        LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    },

    riskAssessment: {
        EMPLOYMENT_STATUS: (value: string) =>
            `EMPLOYMENT_STATUS_${value}` as const,
        FINANCIAL_PROFILE_CONTINUE: 'FINANCIAL_PROFILE_CONTINUE',
    },
    address: {
        ADDRESS_CONTINUE: 'ADDRESS_CONTINUE',
    },
    phoneNumber: {
        PHONE_NUMBER_SMS_CONTINUE: 'PHONE_NUMBER_SMS_CONTINUE',
        PHONE_NUMBER_CONTINUE: 'PHONE_NUMBER_CONTINUE',
        PHONE_NUMBER_SMS: 'PHONE_NUMBER_SMS',
    },
    accountType: {
        CHOOSE_ACCOUNT_TYPE_CONTINUE: 'CHOOSE_ACCOUNT_TYPE_CONTINUE',
    },
    currency: {
        CURRENCY: (value: string) => `CURRENCY_${value}` as const,
        CURRENCY_CONTINUE: 'CURRENCY_CONTINUE',
    },
    proofOfIdentity: {
        PROOF_OF_IDENTITY_QR: 'PROOF_OF_IDENTITY_QR',
        PROOF_OF_IDENTITY_SMS: 'PROOF_OF_IDENTITY_SMS',
        PROOF_OF_IDENTITY_SKIP: 'PROOF_OF_IDENTITY_SKIP',
        PROOF_OF_IDENTITY_UPLOAD_FROM_DEVICE:
            'PROOF_OF_IDENTITY_UPLOAD_FROM_DEVICE',
        PROOF_OF_IDENTITY_POPUP_RETURN: 'PROOF_OF_IDENTITY_POPUP_RETURN',
        PROOF_OF_IDENTITY_HOW_TO_TAKE_PICTURE:
            'PROOF_OF_IDENTITY_HOW_TO_TAKE_PICTURE',
        PROOF_OF_IDENTITY_CAMERA_BLANK_FEED_DETECTED:
            'PROOF_OF_IDENTITY_CAMERA_BLANK_FEED_DETECTED',
        PROOF_OF_IDENTITY_CAMERA_PERMISSION_DENIED:
            'PROOF_OF_IDENTITY_CAMERA_PERMISSION_DENIED',
        PROOF_OF_IDENTITY_POPUP_CONTINUE: 'PROOF_OF_IDENTITY_POPUP_CONTINUE',
    },
    webSdk: {
        WEBSDK_MOBILE_UPLOAD_START: 'WEBSDK_MOBILE_UPLOAD_START',
        WEBSDK_DESKTOP_UPLOAD_START: 'WEBSDK_DESKTOP_UPLOAD_START',
        WEBSDK_HYBRID_UPLOAD_START: 'WEBSDK_HYBRID_UPLOAD_START',
        WEBSDK_MOBILE_UPLOAD_FINISH: 'WEBSDK_MOBILE_UPLOAD_FINISH',
        WEBSDK_DESKTOP_UPLOAD_FINISH: 'WEBSDK_DESKTOP_UPLOAD_FINISH',
        WEBSDK_HYBRID_UPLOAD_FINISH: 'WEBSDK_HYBRID_UPLOAD_FINISH',

        WEBSDK_PREVIEW_STEP_CONFIRM_BUTTON:
            'WEBSDK_PREVIEW_STEP_CONFIRM_BUTTON',
        WEBSDK_PREVIEW_STEP_RETRY_BUTTON: 'WEBSDK_PREVIEW_STEP_RETRY_BUTTON',
    },

    statements: {
        STATEMENTS_CONTINUE: 'STATEMENTS_CONTINUE',
    },

    personalDetails: {
        PERSONAL_DETAILS_CONTINUE: 'PERSONAL_DETAILS_CONTINUE',
    },
} as const;

type ExtractEventStrings<T> = T extends (...args: any[]) => infer R
    ? R
    : T extends object
      ? ExtractEventStrings<T[keyof T]>
      : T;

export type AnalyticsEvent = ExtractEventStrings<
    typeof AmplitudeAnalyticsEvents
>;

export const initAmplitude = (apiKey: string) => {
    amplitude.init(apiKey, { autocapture: true });

    setTimeout(() => {
        const locale = i18n?.global?.locale?.value;
        const branding = UtilityHelper?.isOandaPath?.();
        const isMobile = store?.state?.isDetectedMobileDevice;
        const identifyEvent = new amplitude.Identify();

        // Set only on the first session to track the original acquisition source.
        // Will not be overwritten during subsequent visits.
        identifyEvent.setOnce('app_initial_url', window.location.href);
        identifyEvent.setOnce(
            'app_initial_referrer',
            document.referrer || 'direct'
        );

        if (locale !== undefined) {
            identifyEvent.set(
                'locale',
                LanguageCodeByBranchCode[locale]?.toUpperCase()
            );
            identifyEvent.set('branding', branding ? 'oanda' : 'tms');
            identifyEvent.set('isMobile', isMobile);

            amplitude.identify(identifyEvent);
        }
    }, 100);
};

const isAmplitudeDisabled = () =>
    process.env.NODE_ENV === 'production' && store.state.isInternalDomain;

export const amplitudeSendEvent = (
    event: AnalyticsEvent,
    props?: Record<string, any>
) => {
    if (isAmplitudeDisabled()) return;

    const locale = i18n?.global?.locale?.value;
    const branding = UtilityHelper?.isOandaPath?.();
    const isMobile = store.state.isDetectedMobileDevice;
    const residence = store.state.residenceCountry?.toUpperCase?.().trim?.();

    amplitude.track(event, {
        ...props,
        language: LanguageCodeByBranchCode[locale].toUpperCase(),
        branding: branding ? 'oanda' : 'tms',
        isMobile,
        ...(residence ? { residence } : {}),
    });
};

export const amplitudeSetEmail = (email?: string) => {
    if (!email || isAmplitudeDisabled()) return;

    const normalizedEmail = email.toLowerCase().trim();
    const hashedEmail = CryptoJS.MD5(normalizedEmail).toString();
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('hash_email', hashedEmail);
    amplitude.identify(identifyEvent);
};

export const amplitudeSetResidence = (countryCode: string) => {
    if (isAmplitudeDisabled()) return;

    const identifyEvent = new amplitude.Identify();

    identifyEvent.set('residence', countryCode.toUpperCase().trim());
    amplitude.identify(identifyEvent);
};
